import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { PlayIcon, SlideIcon, Spinner, hueFor } from "../components/Icons";
import { ApiError, api, live } from "../lib/api";
import type { PresentationDetail, Slide, SlideConfig, SlideTypeInfo } from "../lib/types";
import { SlideEditor } from "./SlideEditor";

const SAVE_DEBOUNCE_MS = 500;

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const presentationId = Number(id);

  const [deck, setDeck] = useState<PresentationDetail | null>(null);
  const [types, setTypes] = useState<SlideTypeInfo[]>([]);
  const [selected, setSelected] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  // An empty deck has nothing to edit, so the picker is the screen.
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Edits queue here and flush on a timer, so typing a question is one
  // request instead of one per keystroke.
  const pending = useRef(new Map<number, { question: string; config: SlideConfig }>());
  const timer = useRef<number | undefined>(undefined);

  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    const queued = [...pending.current.entries()];
    if (queued.length === 0) return;
    pending.current.clear();
    setSaving(true);
    try {
      const saved = await Promise.all(
        queued.map(([slideId, patch]) => api.updateSlide(slideId, patch)),
      );
      setFieldErrors([]);
      setDeck((current) =>
        current
          ? {
              ...current,
              slides: current.slides.map(
                (slide) => saved.find((s) => s.id === slide.id) ?? slide,
              ),
            }
          : current,
      );
    } catch (err) {
      if (err instanceof ApiError && err.fields.length) {
        setFieldErrors(err.fields.map((f) => f.message));
      } else {
        setError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }, []);

  const queueSave = useCallback(
    (slide: Slide) => {
      pending.current.set(slide.id, { question: slide.question, config: slide.config });
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  useEffect(() => {
    Promise.all([api.getPresentation(presentationId), api.slideTypes()])
      .then(([detail, slideTypes]) => {
        setDeck(detail);
        setTypes(slideTypes);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) navigate("/admin", { replace: true });
        else setError((err as Error).message);
      });
  }, [presentationId, navigate]);

  // A queued edit must not die with the page. Flush on unmount and on tab
  // hide, which is when a laptop lid closing would otherwise lose it.
  useEffect(() => {
    const onHide = () => void flush();
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      void flush();
    };
  }, [flush]);

  if (error) {
    return (
      <Shell>
        <div className="card" style={{ margin: "40px auto", maxWidth: 520, color: "var(--error)" }}>
          {error}
        </div>
      </Shell>
    );
  }

  if (!deck) {
    return (
      <Shell>
        <div style={{ display: "grid", placeItems: "center", padding: 80 }}>
          <Spinner size={26} />
        </div>
      </Shell>
    );
  }

  const slides = deck.slides;
  const current = slides.length ? slides[Math.min(selected, slides.length - 1)] : null;
  const isEmpty = slides.length === 0;

  function patchSelected(patch: { question?: string; config?: SlideConfig }) {
    if (!current) return;
    const next = { ...current, ...patch };
    setDeck((d) =>
      d ? { ...d, slides: d.slides.map((s) => (s.id === next.id ? next : s)) } : d,
    );
    queueSave(next);
  }

  async function select(index: number) {
    await flush(); // don't let switching slides drop a queued edit
    setSelected(index);
    setShowPicker(false);
    setFieldErrors([]);
  }

  async function addSlide(type: SlideTypeInfo["type"]) {
    await flush();
    const slide = await api.addSlide(deck!.id, type);
    setDeck((d) => (d ? { ...d, slides: [...d.slides, slide] } : d));
    setSelected(slides.length);
    setShowPicker(false);
  }

  async function deleteSlide() {
    if (!current || !confirm("Delete this slide?")) return;
    await flush();
    try {
      await api.deleteSlide(current.id);
      const detail = await api.getPresentation(deck!.id);
      setDeck(detail);
      setSelected((s) => Math.max(0, s - 1));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= slides.length) return;
    await flush();
    const order = slides.map((s) => s.id);
    [order[index], order[target]] = [order[target], order[index]];
    setDeck(await api.reorderSlides(deck!.id, order));
    setSelected(target);
  }

  async function present() {
    await flush(); // a queued question edit must reach the room
    const started = await live.start(deck!.id);
    navigate(`/present/${started.session.id}`);
  }

  async function rename(title: string) {
    if (!title.trim() || title === deck!.title) return;
    setDeck(await api.renamePresentation(deck!.id, title.trim()));
  }

  return (
    <Shell
      crumb={
        <input
          className="input"
          aria-label="Presentation title"
          defaultValue={deck.title}
          onBlur={(e) => void rename(e.target.value)}
          style={{ width: 240, height: 36, fontWeight: 600 }}
        />
      }
      actions={
        <>
          <span className="tiny" style={{ minWidth: 54, textAlign: "right" }}>
            {saving ? "Saving…" : "Saved"}
          </span>
          <button
            className="btn"
            onClick={() => void present()}
            disabled={isEmpty}
            title={isEmpty ? "Add a slide first" : undefined}
          >
            <PlayIcon /> Present
          </button>
        </>
      }
    >
      <div
        style={{
          display: "flex",
          gap: 20,
          padding: 20,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <aside
          style={{
            flex: "0 0 240px",
            // Without this a flex item floors at its content's min-content
            // width (min-width defaults to auto), and the basis is ignored.
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            position: "sticky",
            top: 84,
          }}
        >
          {slides.map((slide, index) => {
            const active = slide.id === current?.id;
            return (
              <div
                key={slide.id}
                onClick={() => void select(index)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 12px 12px 14px",
                  borderRadius: 16,
                  cursor: "pointer",
                  background: active ? "var(--primary-container)" : "var(--surface)",
                  border: `0.5px solid ${active ? "var(--primary)" : "var(--outline)"}`,
                }}
              >
                <span className="tiny" style={{ width: 14 }}>
                  {index + 1}
                </span>
                <SlideIcon type={slide.type} size={20} color={hueFor(index)} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {slide.question || "Untitled"}
                  </div>
                  <div className="tiny">{slide.type_label}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <button
                    className="btn btn-text"
                    aria-label="Move slide up"
                    disabled={index === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      void move(index, -1);
                    }}
                    style={{ padding: "0 4px", lineHeight: 1 }}
                  >
                    ▲
                  </button>
                  <button
                    className="btn btn-text"
                    aria-label="Move slide down"
                    disabled={index === slides.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      void move(index, 1);
                    }}
                    style={{ padding: "0 4px", lineHeight: 1 }}
                  >
                    ▼
                  </button>
                </div>
              </div>
            );
          })}
          {!isEmpty && (
            <button className="btn btn-outline" onClick={() => setShowPicker((v) => !v)}>
              + Add slide
            </button>
          )}
        </aside>

        <div
          style={{
            flex: 1,
            minWidth: 300,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {(showPicker || isEmpty) && (
            <div className="card" style={{ animation: "cardIn 250ms var(--ease-default)" }}>
              <h2 style={{ fontSize: 17, marginBottom: 4 }}>
                {isEmpty ? "Add your first question" : "Interactive questions"}
              </h2>
              <p className="muted" style={{ marginBottom: 16 }}>
                Pick a type — responses appear live as the audience answers.
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                  gap: 10,
                }}
              >
                {types.map((type, index) => (
                  <button
                    key={type.type}
                    onClick={() => void addSlide(type.type)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: 16,
                      borderRadius: 16,
                      border: "0.5px solid var(--outline)",
                      background: "var(--surface)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <SlideIcon type={type.type} size={26} color={hueFor(index)} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{type.label}</span>
                    <span className="tiny">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {current && (
            <SlideEditor
              key={current.id}
              slide={current}
              accent={hueFor(slides.indexOf(current))}
              onChange={patchSelected}
              onDelete={() => void deleteSlide()}
              canDelete
              fieldErrors={fieldErrors}
            />
          )}

          <div
            className="card"
            style={{ background: "var(--surface-container)", borderStyle: "dashed" }}
          >
            <h3 style={{ fontSize: 15, marginBottom: 4 }}>How people will join</h3>
            <p className="muted">
              When you present, every slide shows a QR code and a numeric code. Participants open{" "}
              <b>/user</b> on their phone, scan or type the code, and answer along. They can't
              change slides — you drive.
            </p>
          </div>
        </div>
      </div>
    </Shell>
  );
}
